/**
 * ProgramsList — athlete-facing program library, filtered by category.
 *
 * Flow: TrainingCategories → tap a category → land here showing all programs
 * in that category. Tap a program → ProgramDetail. Sachin 2026-02-27 spec:
 * programs are the containers; workouts only show inside a chosen program.
 *
 * Strength category also surfaces sub-tabs (Full / Lower / Upper) that map
 * to the body_focus field on each program.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowLeft, ArrowRight, Lock, Clock, Calendar, Layers } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
    daily: "Daily Workouts", speed: "Speed", strength: "Strength",
    stamina: "Stamina", suppleness: "Suppleness", skill: "Skill",
};
const DIFFICULTY_TONE = {
    beginner: "text-emerald-300",
    intermediate: "text-sky-300",
    advanced: "text-amber-300",
};

export default function ProgramsList() {
    const { user, loading, activeAthleteId } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const category = searchParams.get("category") || "";
    const bodyFocus = searchParams.get("body_focus") || "";
    const athleteId = searchParams.get("athlete_id") || activeAthleteId || "";
    const [rows, setRows] = useState(null);

    useEffect(() => {
        if (!loading && !user) navigate("/", { replace: true });
    }, [loading, user, navigate]);

    useEffect(() => {
        if (!user || !category) return;
        const params = { category };
        if (category === "strength" && bodyFocus) params.body_focus = bodyFocus;
        if (athleteId) params.athlete_id = athleteId;
        axios.get(`${API}/programs`, { params, withCredentials: true })
            .then((r) => setRows(r.data || []))
            .catch(() => setRows([]));
    }, [user, category, bodyFocus, athleteId]);

    const setFocus = (v) => {
        const next = new URLSearchParams(searchParams);
        if (v) next.set("body_focus", v); else next.delete("body_focus");
        setSearchParams(next);
    };

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    if (!category) {
        // Defensive — direct hit without a category should bounce back to the
        // Programs landing so the athlete picks one first.
        navigate("/dashboard/workouts", { replace: true });
        return null;
    }

    return (
        <div data-testid="programs-list" className="min-h-screen bg-brand-obsidian text-white pt-20 pb-24 md:pb-12 px-4 md:px-8">
            <div className="max-w-3xl mx-auto">
                <Link
                    to={`/dashboard/workouts${athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : ""}`}
                    className="inline-flex items-center gap-1 text-xs text-zinc-300 hover:text-brand-red mb-3"
                >
                    <ArrowLeft className="w-3 h-3" /> All categories
                </Link>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-1">Programs</div>
                <h1 className="font-display text-3xl md:text-5xl tracking-wider mb-2">
                    {CATEGORY_LABELS[category] || category}
                </h1>
                <p className="text-sm text-zinc-400 mb-6">
                    Pick a program. Each one is a multi-week plan with the workouts already laid out.
                </p>

                {category === "strength" && (
                    <div
                        data-testid="programs-body-focus-tabs"
                        className="flex flex-wrap items-center gap-1.5 mb-5"
                    >
                        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mr-1">Focus</span>
                        {[
                            { value: "", label: "All" },
                            { value: "full_body", label: "Full Body" },
                            { value: "lower_body", label: "Lower Body" },
                            { value: "upper_body", label: "Upper Body" },
                        ].map((opt) => (
                            <button
                                key={opt.value}
                                data-testid={`programs-focus-${opt.value || "all"}`}
                                onClick={() => setFocus(opt.value)}
                                className={`text-[10px] tracking-[0.2em] uppercase border px-3 py-1.5 ${
                                    bodyFocus === opt.value
                                        ? "border-sky-500 text-sky-300 bg-sky-950/20"
                                        : "border-zinc-800 text-zinc-300 hover:text-white"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}

                {rows === null ? (
                    <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
                ) : rows.length === 0 ? (
                    <div data-testid="programs-empty" className="border border-zinc-800 rounded-2xl p-8 text-center text-sm text-zinc-400">
                        No programs in this category yet. Your coach will publish some soon.
                    </div>
                ) : (
                    <div className="space-y-3 md:space-y-4">
                        {rows.map((p) => {
                            const locked = p.locked;
                            const href = `/dashboard/programs/${p.program_id}${athleteId ? `?athlete_id=${encodeURIComponent(athleteId)}` : ""}`;
                            const Wrapper = locked ? "div" : Link;
                            const props = locked ? {} : { to: href };
                            return (
                                <Wrapper
                                    key={p.program_id}
                                    {...props}
                                    data-testid={`program-card-${p.program_id}`}
                                    className={`group block border-2 rounded-2xl px-4 md:px-5 py-4 md:py-5 select-none ${
                                        locked
                                            ? "border-zinc-800 opacity-60 cursor-not-allowed"
                                            : "border-zinc-800 hover:border-brand-red cursor-pointer transition-colors"
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <h2 className="font-display text-xl md:text-2xl tracking-wider text-white">
                                                    {p.title}
                                                </h2>
                                                {locked && (
                                                    <span className="text-[10px] tracking-[0.25em] uppercase border border-zinc-700 text-zinc-500 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                                        <Lock className="w-2.5 h-2.5" /> Locked
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-4 text-[11px] tracking-[0.15em] uppercase text-zinc-400 flex-wrap">
                                                {p.difficulty && (
                                                    <span className={DIFFICULTY_TONE[p.difficulty] || "text-zinc-300"}>
                                                        {p.difficulty}
                                                    </span>
                                                )}
                                                <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {p.duration_weeks || 4} wk</span>
                                                <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.days_per_week || 5}×/wk</span>
                                                {p.body_focus && (
                                                    <span className="text-sky-300">
                                                        {p.body_focus.replace("_", " ")}
                                                    </span>
                                                )}
                                            </div>
                                            {p.description && (
                                                <p className="text-xs md:text-sm text-zinc-400 mt-2 leading-relaxed line-clamp-2">
                                                    {p.description}
                                                </p>
                                            )}
                                        </div>
                                        {!locked && (
                                            <ArrowRight className="w-5 h-5 text-zinc-500 group-hover:text-white transition-colors flex-shrink-0 mt-1" />
                                        )}
                                    </div>
                                </Wrapper>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
